import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { 
  ExpandMore as ExpandMoreIcon, 
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import SymbolLookup from './SymbolLookup';
import { StockService } from '../services/stockService';
import { StockLookupService, SymbolSearchResult } from '../services/stockLookupService';
import { tradingConfig } from '../config/tradingConfig';
import HelpTooltip from './HelpTooltip';

interface AddStockDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (stock: any) => Promise<void>;
}

const AddStockDialog: React.FC<AddStockDialogProps> = ({ open, onClose, onAdd }) => {
  const [loading, setLoading] = useState(false);
  const [loadingStockInfo, setLoadingStockInfo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingFieldValues, setLoadingFieldValues] = useState(false);
  const [expandedSection, setExpandedSection] = useState(false);
  const [showPivotWarning, setShowPivotWarning] = useState(false);
  
  // Basic fields (required)
  const [symbol, setSymbol] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [pivotPrice, setPivotPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  
  // Advanced fields (optional)
  const [weeklyBaseType, setWeeklyBaseType] = useState('');
  const [weeklyBaseStage, setWeeklyBaseStage] = useState('');
  const [avgDailyVolume, setAvgDailyVolume] = useState('');
  const [industryName, setIndustryName] = useState('');
  const [epsRating, setEpsRating] = useState('');
  const [rsRating, setRsRating] = useState('');
  const [compRating, setCompRating] = useState('');
  const [currentVolume, setCurrentVolume] = useState('');
  const [marketCap, setMarketCap] = useState('');
  
  // Dropdown options
  const [fieldOptions, setFieldOptions] = useState({
    baseTypes: [] as string[],
    baseStages: [] as string[],
    adRatings: [] as string[],
    smrRatings: [] as string[],
    industries: [] as string[],
  });

  useEffect(() => {
    if (open) {
      resetForm();
      if (fieldOptions.baseTypes.length === 0) {
        loadFieldValues();
      }
    }
  }, [open]);

  const resetForm = () => {
    setSymbol('');
    setCompanyName('');
    setCurrentPrice('');
    setPivotPrice('');
    setStopLoss('');
    setTargetPrice('');
    setWeeklyBaseType('');
    setWeeklyBaseStage('');
    setAvgDailyVolume('');
    setIndustryName('');
    setEpsRating('');
    setRsRating('');
    setCompRating('');
    setCurrentVolume('');
    setMarketCap('');
    setError(null);
    setExpandedSection(false);
    setShowPivotWarning(false);
  };

  const loadFieldValues = async () => {
    setLoadingFieldValues(true);
    try {
      const values = await StockService.getUniqueFieldValues();
      setFieldOptions(values);
    } catch (error) {
      console.error('Error loading field values:', error);
    }
    setLoadingFieldValues(false);
  };

  const handleStockSelected = useCallback(async (stock: SymbolSearchResult) => {
    setCompanyName(stock.name);
    
    // Fetch latest price info
    setLoadingStockInfo(true);
    try {
      const stockInfo = await StockLookupService.getStockInfo(stock.symbol);
      setCurrentPrice(stockInfo.currentPrice.toFixed(2));
      // Don't pre-populate volume - IEX feed volume is unreliable
      // Volume will be populated correctly during scheduled updates using SIP feed
      // setCurrentVolume(stockInfo.volume.toString());
      
      // Note: Alpaca doesn't provide market cap, P/E ratios, or industry info
      // These would need to come from a premium data provider
    } catch (error) {
      console.error('Error fetching stock info:', error);
      // Don't show error - user can still enter price manually
    } finally {
      setLoadingStockInfo(false);
    }
  }, []);

  const handleSymbolChange = useCallback((newSymbol: string | null, name?: string) => {
    setSymbol(newSymbol || '');
    if (!newSymbol) {
      setCompanyName('');
    } else if (name) {
      setCompanyName(name);
    }
  }, []);

  const calculateTargetFromPivot = () => {
    if (pivotPrice) {
      const pivot = parseFloat(pivotPrice);
      const target = StockService.calculateTargetPrice(pivot);
      setTargetPrice(target.toFixed(2));
    }
  };

  const calculateStopLossFromPivot = () => {
    if (pivotPrice) {
      const pivot = parseFloat(pivotPrice);
      const stop = pivot * (1 - tradingConfig.stopLossPercentage / 100);
      setStopLoss(stop.toFixed(2));
    }
  };

  const handleSubmit = async () => {
    if (!symbol || !companyName || !currentPrice) {
      setError('Please fill in all required fields');
      return;
    }

    // Show warning if no pivot price but don't block
    if (!pivotPrice) {
      setShowPivotWarning(true);
    }

    setLoading(true);
    setError(null);

    try {
      const stockData: any = {
        symbol: symbol.toUpperCase(),
        companyName,
        currentPrice: parseFloat(currentPrice),
      };

      // Add optional fields if provided
      if (pivotPrice) stockData.pivotPrice = parseFloat(pivotPrice);
      if (stopLoss) stockData.stopLoss = parseFloat(stopLoss);
      if (targetPrice) stockData.targetPrice = parseFloat(targetPrice);
      
      // Add advanced fields if provided
      if (weeklyBaseType) stockData.weeklyBaseType = weeklyBaseType;
      if (weeklyBaseStage) stockData.weeklyBaseStage = weeklyBaseStage;
      if (avgDailyVolume) stockData.avgDailyVolume = parseFloat(avgDailyVolume);
      if (currentVolume) stockData.volume = parseInt(currentVolume);
      if (industryName) stockData.industryName = industryName;
      if (epsRating) stockData.epsRating = parseInt(epsRating);
      if (rsRating) stockData.rsRating = parseInt(rsRating);
      if (compRating) stockData.compRating = parseInt(compRating);
      if (marketCap) stockData.marketCap = parseFloat(marketCap);

      await onAdd(stockData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Stock to Watchlist</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {showPivotWarning && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setShowPivotWarning(false)}>
                OK
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Note:</strong> Pivot price is required for the Recommendation Service to consider this stock. 
              You can add it later when you have determined the appropriate entry point.
            </Typography>
          </Alert>
        )}
        
        {/* Basic Fields */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SymbolLookup
                value={symbol}
                onChange={handleSymbolChange}
                onStockSelected={handleStockSelected}
                label="Stock Symbol"
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                InputProps={{
                  endAdornment: loadingStockInfo && (
                    <CircularProgress size={20} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Current Price"
                type="number"
                value={currentPrice}
                onChange={(e) => setCurrentPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Current Volume"
                type="number"
                value={currentVolume}
                onChange={(e) => setCurrentVolume(e.target.value)}
                helperText="15-min delayed"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Market Cap (M)"
                type="number"
                value={marketCap}
                onChange={(e) => setMarketCap(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="In millions"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Price Targets */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Price Targets
            </Typography>
            <Chip 
              label="Optional" 
              size="small" 
              color="info" 
              variant="outlined" 
              sx={{ ml: 1 }}
            />
            <Box component="span" sx={{ ml: 1 }}>
              <HelpTooltip 
                title="Price targets can be added later. Pivot price is required for recommendations." 
              />
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pivot Price"
                type="number"
                value={pivotPrice}
                onChange={(e) => setPivotPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpTooltip title="Entry point from technical analysis. Required for recommendations." />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stop Loss"
                type="number"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={calculateStopLossFromPivot} 
                        size="small"
                        disabled={!pivotPrice}
                      >
                        <CalculateIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={`Default: ${tradingConfig.stopLossPercentage}% below pivot`}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Target Price"
                type="number"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={calculateTargetFromPivot} 
                        size="small"
                        disabled={!pivotPrice}
                      >
                        <CalculateIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={`Default: ${tradingConfig.targetPricePercentage}% above pivot`}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Advanced Fields (Optional) */}
        <Accordion 
          expanded={expandedSection}
          onChange={(_, isExpanded) => setExpandedSection(isExpanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight="bold">
              Advanced Fields (Optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Base Type</InputLabel>
                  <Select
                    value={weeklyBaseType}
                    onChange={(e) => setWeeklyBaseType(e.target.value)}
                    label="Base Type"
                  >
                    {loadingFieldValues ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      fieldOptions.baseTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Base Stage</InputLabel>
                  <Select
                    value={weeklyBaseStage}
                    onChange={(e) => setWeeklyBaseStage(e.target.value)}
                    label="Base Stage"
                  >
                    {fieldOptions.baseStages.map((stage) => (
                      <MenuItem key={stage} value={stage}>
                        Stage {stage}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="50-Day Avg Volume"
                  type="number"
                  value={avgDailyVolume}
                  onChange={(e) => setAvgDailyVolume(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <HelpTooltip title="Will be calculated automatically if not provided" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={industryName}
                    onChange={(e) => setIndustryName(e.target.value)}
                    label="Industry"
                  >
                    {fieldOptions.industries.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Ratings (1-99)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Comp Rating"
                  type="number"
                  value={compRating}
                  onChange={(e) => setCompRating(e.target.value)}
                  inputProps={{ min: 1, max: 99 }}
                  helperText="Composite rating"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="EPS Rating"
                  type="number"
                  value={epsRating}
                  onChange={(e) => setEpsRating(e.target.value)}
                  inputProps={{ min: 1, max: 99 }}
                  helperText="Earnings strength"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="RS Rating"
                  type="number"
                  value={rsRating}
                  onChange={(e) => setRsRating(e.target.value)}
                  inputProps={{ min: 1, max: 99 }}
                  helperText="Relative strength"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !symbol || !companyName || !currentPrice}
        >
          {loading ? <CircularProgress size={24} /> : 'Add Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStockDialog;